import type { RowSelectionState, Updater } from "@tanstack/react-table"
import type { FC, RefObject } from "react"
import { useMemo } from "react"

import { useTransferProductSelectionColumns } from "../../hooks"

import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { InventoryByProductItem } from "@/graphql/codegen/graphql"

interface TransferProductSelectionTableProps {
  data: InventoryByProductItem[]
  addedInventories: InventoryByProductItem[]
  selectedInventories: InventoryByProductItem[]
  loading: boolean
  containerRef: RefObject<HTMLDivElement>
  onSelectionChange: (inventories: InventoryByProductItem[]) => void
  onScroll: () => void
}

export const TransferProductSelectionTable: FC<TransferProductSelectionTableProps> = ({
  data,
  addedInventories,
  selectedInventories,
  loading,
  containerRef,
  onSelectionChange,
  onScroll,
}) => {
  const columns = useTransferProductSelectionColumns()

  const addedProductIds = useMemo(
    () => new Set(addedInventories.map((addedInventory) => addedInventory.product.productId)),
    [addedInventories]
  )

  const rowSelection = useMemo(() => {
    return selectedInventories.reduce((acc, selectedInventory) => {
      acc[selectedInventory.product.productId] = true
      return acc
    }, {} as RowSelectionState)
  }, [selectedInventories])

  const handleRowSelectionChange = (updater: Updater<RowSelectionState>) => {
    if (typeof updater !== "function") return

    const currentSelection = updater(rowSelection)

    const visibleProductIds = new Set(data.map((inventory) => String(inventory.product.productId)))
    const retainedInventories = selectedInventories.filter(
      (selectedInventory) => !visibleProductIds.has(String(selectedInventory.product.productId))
    )
    const visibleSelectedInventories = data.filter((inventory) => currentSelection[String(inventory.product.productId)])
    onSelectionChange([...retainedInventories, ...visibleSelectedInventories])
  }

  return (
    <AerosBaseTable
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          getRowId: (row) => String(row.product.productId),
          state: { rowSelection, isLoading: loading },
          onRowSelectionChange: handleRowSelectionChange,
          enableRowSelection: (row) => !addedProductIds.has(row.original.product.productId),
          enableMultiRowSelection: true,
        },
      }}
      noDataMessage='No products found'
      slotProps={{ container: { ref: containerRef, onScroll } }}
    />
  )
}
